import * as Sentry from '@sentry/browser';
import { all, call, delay, put, select, take } from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';

import { bffAPI, iframeAPI } from '@/api';
import { setAuthMode } from '@/api/utils/auth.provider';
import { goalReached, YMGoals } from '@/infrastructure/ym';
import * as auth from '@/model/auth/actions';
import { makeSelectInitialData } from '@/model/auth/selectors';
import { makeSelectIsFrameView, makeSelectPartner } from '@/model/global/selectors';
import * as identification from '@/model/identification/actions';
import createCache, { setCacheMode } from '@/utils/cache';
import { ActionWithPayload, takeEveryTypesafe } from '@/utils/redux';

import * as actions from './actions';
import { ApplicationType } from './types';
import { withGlobalLock } from './utils';

const localeCache = createCache<string>('MNX_LOCALE');

function* addNotification({
  payload: { timeout = 6000, ...notification },
}: ActionWithPayload<actions.AddNotificationActionPayload>): Generator {
  const id = uuid();
  yield* put(actions.storeSaveNotification({ id, ...notification }).pure);
  if (timeout !== 'infinite') {
    yield* delay(timeout);
    yield* put(actions.storeRemoveNotification({ id }).pure);
  }
}

function* initFrame(): Generator {
  let isFrame;
  try {
    isFrame = !!new URLSearchParams(window.location.search).get('frame');
    if (isFrame) {
      setCacheMode('mem');
      setAuthMode('header');
    }
  } catch (e) {
    isFrame = false;
    console.error(e);
    Sentry.captureException(e instanceof Error ? e : new Error());
  }
  goalReached(isFrame ? YMGoals.IFRAME_MODE : YMGoals.NO_IFRAME_MODE);
  yield* put(actions.storeFrameMode({ isFrame }).pure);
}

function* initLocale(): Generator {
  let locale;
  try {
    locale = new URLSearchParams(window.location.search).get('lang');
  } catch (e) {
    console.error(e);
    Sentry.captureException(e instanceof Error ? e : new Error());
  }
  if (!locale) {
    try {
      locale = localeCache.read();
    } catch (e) {
      console.error(e);
      Sentry.captureException(e instanceof Error ? e : new Error());
    }
  }
  if (!locale) {
    locale = window.env.DEFAULT_LOCALE;
  }
  yield* put(actions.storeLocale({ locale }).pure);
}

function* initApplicationType(): Generator {
  const { pathname } = window.location;
  let type = ApplicationType.WITHDRAWAL;
  if (pathname.startsWith(`/${ApplicationType.DEMO}`)) {
    type = ApplicationType.DEMO;
  } else if (pathname.startsWith(`/${ApplicationType.WALLET}`)) {
    type = ApplicationType.WALLET;
  } else if (pathname.startsWith(`/${ApplicationType.WITHDRAWAL}`)) {
    type = ApplicationType.WITHDRAWAL;
  }
  yield* put(actions.storeAppType({ type }).pure);
}

function* requestToggle(): Generator {
  try {
    const flags = yield* call(bffAPI.queryToggle);
    yield* put(actions.storeToggle({ flags }).pure);
  } catch (e) {
    console.log(e);
    yield* put(actions.storeToggle({ error: typeof e === 'string' ? e : 'unable to fetch toggle', flags: {} }).pure);
  }
}

function* init(): Generator {
  yield* put(actions.notifyGlobalInitializationStarted().pure);
  // add here initialization finish listeners manually
  yield* all([take(auth.notifyAuthorizationFinished.type), take(identification.notifyInitializationFinished.type)]);
  const authData = yield* select(makeSelectInitialData());
  yield* put(actions.storeInitializationState({ partner: authData?.partner }).pure);
}

const selectPartner = makeSelectPartner();
const selectIsFrame = makeSelectIsFrameView();

function* logout(): Generator {
  localeCache.clear();
  const partner = yield* select(selectPartner);
  const isFrame = yield* select(selectIsFrame);
  yield* put(auth.storeLogout().pure);
  yield* put(actions.storeLogout().pure);
  yield* put(actions.notifyLogout().pure);
  if (isFrame) {
    iframeAPI.sendLoggedOut();
  } else if (partner?.returnUrl) {
    window.location.assign(partner.returnUrl);
  }
}

export default function* rootSaga(): Generator {
  yield* takeEveryTypesafe(actions.initAppType, withGlobalLock(initApplicationType, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(actions.initFrame, withGlobalLock(initFrame, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(actions.initLocale, withGlobalLock(initLocale, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(actions.init, withGlobalLock(init, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(actions.logout, withGlobalLock(logout, 'common.messages.operations.logout'));
  yield* takeEveryTypesafe(actions.requestToggle, withGlobalLock(requestToggle, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(actions.addNotification, addNotification);
}
